import { useState } from "react";
import type { Trip, ThemeClasses } from "../types";
import {
  getStatusToneForTrip,
  getStatusStyle,
  getStatusLabel,
  getPriorityTone,
  getPriorityLabel,
  formatTripDate,
  tripSubtitle,
} from "../utils";
import {
  Badge,
  Button,
  EntityCard,
  EntityCardActions,
  EntityCardBody,
  EntityCardFooter,
  EntityCardHeader,
  EntityCardRow,
} from "../../../../../../components/ui";
import type { TFunction } from "i18next";
import { CRUDTooltip } from "../../../../../../components/base/CRUDTooltip";
import { useOpenChat } from "../../../../../../pages/support/hooks/useOpenChat";
import { activeThemeId } from "../../../../../../config/theme";
import { ACCESS_NODES } from "../../../../../../config/access/nodes";
import { useCanWrite } from "../../../../../../hooks/useCanWrite";

const isChatEnabled = activeThemeId === "wayak";

interface TripGridCardProps {
  trip: Trip;
  themeClasses: ThemeClasses;
  onEdit: (trip: Trip) => void;
  onDetails: (trip: Trip) => void;
  onAssignments: (trip: Trip) => void;
  onDelete: (trip: Trip) => void;
  onOpenChatModal: (trip: Trip) => void;
  /** Cotizador de costos operativos; ausente cuando la app no está habilitada. */
  onQuote?: (trip: Trip) => void;
  t: TFunction;
  disableCRUD: boolean;
}

export function TripGridCard({
  trip,
  onEdit,
  onDetails,
  onAssignments,
  onDelete,
  onOpenChatModal,
  onQuote,
  t,
  disableCRUD,
}: TripGridCardProps) {
  const { tryOpenExistingTripChat, isReady } = useOpenChat();
  const [isOpeningChat, setIsOpeningChat] = useState(false);
  const canWrite = useCanWrite(ACCESS_NODES.adminTrips);

  const assignmentsCount = trip.assignments?.length || 0;
  const activeAssignments =
    trip.assignments?.filter(
      (a) => a.status === "active" || a.status === "pending",
    ).length || 0;
  const canManage = canWrite;

  const handleOpenChat = async () => {
    if (!isReady || isOpeningChat) return;
    setIsOpeningChat(true);
    try {
      const opened = await tryOpenExistingTripChat(trip.id);
      if (!opened) {
        onOpenChatModal(trip);
      }
    } finally {
      setIsOpeningChat(false);
    }
  };

  return (
    <EntityCard>
      <EntityCardHeader
        icon={<i className="ri-route-line" />}
        iconTone="info"
        title={trip.title || t("trips.untitled")}
        subtitle={tripSubtitle(trip)}
        actions={
          <>
            {trip.status?.name && (
              <Badge tone={getStatusToneForTrip(trip)} style={getStatusStyle(trip)}>
                {getStatusLabel(trip, t)}
              </Badge>
            )}
            {isChatEnabled && (
              <Button
                variant="outline"
                size="sm"
                iconOnly
                loading={isOpeningChat}
                icon={<i className="ri-chat-3-line" />}
                onClick={handleOpenChat}
                disabled={!isReady || isOpeningChat}
                title={t("trips.actions.openChat", "Abrir chat del viaje")}
              />
            )}
          </>
        }
      />

      <EntityCardBody>
        {trip.priority && (
          <div>
            <Badge tone={getPriorityTone(trip.priority)}>
              {getPriorityLabel(trip.priority, t)}
            </Badge>
          </div>
        )}

        <EntityCardRow label={t("trips.assignments.title")}>
          <button
            type="button"
            onClick={() => onAssignments(trip)}
            title={t("trips.assignments.open")}
            className="inline-flex items-center gap-1 text-primary-500 hover:underline"
          >
            {activeAssignments}/{assignmentsCount} {t("trips.assignments.devices")}
            <i className="ri-external-link-line" />
          </button>
        </EntityCardRow>

        {trip.description && (
          <EntityCardRow label={t("trips.fields.description")} align="start">
            {trip.description}
          </EntityCardRow>
        )}
      </EntityCardBody>

      <EntityCardActions>
        {canManage && (
          <CRUDTooltip className="flex-1">
            <Button
              variant="outline"
              size="sm"
              fullWidth
              onClick={() => onEdit(trip)}
              disabled={disableCRUD}
            >
              {t("trips.actions.edit")}
            </Button>
          </CRUDTooltip>
        )}

        <Button size="sm" className="flex-1" onClick={() => onDetails(trip)}>
          {t("trips.actions.details")}
        </Button>

        {onQuote && (
          <Button
            variant="outline"
            size="sm"
            iconOnly
            icon={<i className="ri-calculator-line" />}
            onClick={() => onQuote(trip)}
            disabled={disableCRUD}
            className="shrink-0"
            title={t("trips.actions.quote")}
          />
        )}

        {canManage && (
          <CRUDTooltip>
            <Button
              variant="dangerSoft"
              size="sm"
              iconOnly
              icon={<i className="ri-delete-bin-line" />}
              onClick={() => onDelete(trip)}
              disabled={disableCRUD}
              className="shrink-0"
              title={t("trips.actions.delete")}
            />
          </CRUDTooltip>
        )}
      </EntityCardActions>

      <EntityCardFooter>
        {t("trips.fields.createdAt")}: {formatTripDate(trip.createdAt)}
      </EntityCardFooter>
    </EntityCard>
  );
}
